import { getClemanClientByClient, getClemanClientByServer } from '@/apis'
import { Tenant, getTenantByServer } from '@/apis/tenant/tenant'
import { getCurrentUser } from '@/apis/user/currentUser'
import { setUserBindByClient } from '@/apis/user/userBind'
import { AuthingConfigContext } from '@/components/authingWrapper'
import { CLEWORD_DEFAULT_TITLE } from '@/constant'
import { useWechatAuth } from '@/hooks/useWechat'
import {
  getNamespaceAndLocationByServer,
  getUserAgentByServer,
  getWechatIdps
} from '@/utils'
import { getBasePath } from '@/utils/getBasePath'
import getRedirect from '@/utils/getRedirect'
import { getTokenByClient, getTokenByServe, setToken } from '@/utils/token'
import { GuardProvider, useGuard } from '@authing/guard-react'
import '@authing/guard-react/dist/esm/guard.min.css'
import * as Sentry from '@sentry/nextjs'
import { useBoolean } from 'ahooks'
import {
  AuthenticationClient,
  CommonMessage,
  SocialConnectionProvider,
  User
} from 'authing-js-sdk'
import clsx from 'clsx'
import { GetServerSideProps, GetServerSidePropsContext } from 'next'
import Head from 'next/head'
import NEXTImage from 'next/image'
import { useRouter } from 'next/router'
import { useContext, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { NamespaceAndLocation } from './common'
import style from './login.module.scss'

export const handleGetTenantByServer = async (
  namespace: string,
  context: GetServerSidePropsContext
) => {
  if (namespace) {
    try {
      const tenant = await getTenantByServer({ namespace, context })
      if (tenant.data.tenant) {
        return tenant.data.tenant
      }
    } catch (error) {
      console.log('获取租户信息出错', error)
    }
  } else {
    return {
      id: '',
      name: CLEWORD_DEFAULT_TITLE,
      namespace: '',
      logo: 'https://res.pandateacher.com/K9CBKKNV1636358161242.png',
      authingIdps: []
    }
  }
}

export const handleGetUserByServer = async (
  context: GetServerSidePropsContext
) => {
  const token = getTokenByServe(context)
  try {
    const serverClient = getClemanClientByServer(token, context)
    const user = await getCurrentUser(serverClient)
    return user.data.currentUser
  } catch (error) {
    // Sentry.setContext(`ClemanError`, {...error, scene: 'Server-Side'})
    // Sentry.setContext(`user`, { token })
    // Sentry.captureMessage(`Cleman fetch User fail`)
    console.log(error.message || error)
    return null
  }
}

const handleGetUserByClient = async () => {
  const token = getTokenByClient()
  try {
    const client = getClemanClientByClient(token)
    const user = await getCurrentUser(client)
    return user.data.currentUser
  } catch (error) {
    console.log(error.message || error)
    return null
  }
}

function getImageAspectRatio(url: string) {
  return new Promise<number>((resolve, reject) => {
    const img = new Image() as any
    img.crossOrigin = 'Anonymous' // 允许跨域请求
    img.onload = function () {
      // 计算宽高比
      const aspectRatio = img.naturalWidth / img.naturalHeight
      resolve(aspectRatio)
    }
    img.onerror = function () {
      reject(new Error('Failed to load image'))
    }

    img.src = url
  })
}

const changeLoginLogoStyle = (logo: string) => {
  if (logo) {
    getImageAspectRatio(logo).then((aspectRatio) => {
      if (aspectRatio >= 2.5) {
        const authingLoginHeaderNode = document.querySelector(
          '.g2-view-header'
        ) as HTMLElement
        const authingLoginLogoNode = document.querySelector(
          '.g2-view-header>img'
        ) as HTMLImageElement
        if (authingLoginHeaderNode && authingLoginLogoNode) {
          authingLoginHeaderNode.style.flexDirection = 'column'
          authingLoginLogoNode.style.width = 'auto'
        }
      }
    })
  }
}

const changeRegisterLogoStyle = (logo: string) => {
  if (logo) {
    getImageAspectRatio(logo).then((aspectRatio) => {
      if (aspectRatio >= 2.5) {
        const authingRegisterHeaderNode = document.querySelector(
          '.g2-view-header'
        ) as HTMLElement
        const authingRegisterLogoNode = document.querySelector(
          '.g2-view-header>img'
        ) as HTMLImageElement
        if (authingRegisterHeaderNode && authingRegisterLogoNode) {
          authingRegisterHeaderNode.style.display = 'flex'
          authingRegisterHeaderNode.style.alignItems = 'center'
          authingRegisterHeaderNode.style.flexWrap = 'nowrap'
          authingRegisterHeaderNode.style.flexDirection = 'column'
          authingRegisterLogoNode.style.width = 'auto'
        }
      }
    })
  }
}

const onObScenesChange = (logoUrl: string) => {
  const containerNode = document.querySelector(
    '.authing-g2-render-module.authing-g2-render-module-normal'
  )

  const observer = new MutationObserver((mutations) => {
    // 突变列表，包含了变动的详细信息
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList') {
        // console.log('子节点被添加或删除');
        if (
          Array.from(mutation.addedNodes).some((node) =>
            (node as Element).classList.contains('g2-view-register')
          )
        ) {
          changeRegisterLogoStyle(logoUrl)
        } else if (
          Array.from(mutation.addedNodes).some((node) =>
            (node as Element).classList.contains('g2-view-login')
          )
        ) {
          changeLoginLogoStyle(logoUrl)
        }
      } else if (mutation.type === 'attributes') {
        console.log('属性被修改', mutation.attributeName)
      }
    })
  })

  // 配置观察者选项，指定需要观察的变动类型
  const config = {
    childList: true, // 观察子节点的增减
    attributes: false, // 不观察属性变动，如果需要可以设置为 true
    subtree: true // 观察后代节点
  }

  // 启动观察者
  observer.observe(containerNode, config)
}

interface Props extends NamespaceAndLocation {
  tenant: Tenant
  isWechatBrowser: boolean
  isPcBrowser: boolean
  AUTHING_APP_ID: string
  AUTHING_APP_HOST: string
}

const Login = ({
  tenant,
  _location,
  namespace,
  isWechatBrowser,
  isPcBrowser,
  AUTHING_APP_HOST,
  AUTHING_APP_ID
}: Props) => {
  const guard = useGuard()

  const [cusLoginComponentVisible, setCusLoginComponentVisibleActions] =
    useBoolean(true)
  const [isHidden, setHidden] = useBoolean(false)
  const [isAll, setAll] = useBoolean(true)
  const [isInitAuthing, setInitAuthing] = useBoolean(false)
  const router = useRouter()

  const wechatIdps = getWechatIdps({ tenant })
  const [currentLoginInfo, setCurrentLoginInfo] = useState(null)

  const isShowWechatAuth = useMemo(() => {
    if (namespace) {
      return !!(
        (isWechatBrowser && wechatIdps[SocialConnectionProvider.WECHATMP]) ||
        (isPcBrowser && wechatIdps[SocialConnectionProvider.WECHATPC])
      )
    } else {
      return isWechatBrowser || isPcBrowser
    }
  }, [namespace, wechatIdps])

  const onBeforeLogin = (loginParams: any) => {
    setCurrentLoginInfo(loginParams)
    return true
  }

  const onLogin = async (user: User) => {
    const clemanClient = getClemanClientByClient(user.token)

    setToken(user.token)

    await setUserBindByClient()
    clemanClient.wsClient?.close()
    const redirect = getRedirect(_location, router.query as any)

    if (router.query.type === 'fanbook') {
      await handleGetUserByClient()
        .then((res) => {
          if (!res.fanbookUser?.id) {
            // TODO 暂时固定只有 ai.cleword.com 租户使用fanbook授权,而且该租户没有微信登录
            location.replace(
              `https://oauth.fanbook.mobi/authorize?response_type=code&client_id=514987181509578752&state=https%3A%2F%2Fai.cleword.com%2Fauth%2Foauth%2Ffanbook`
            )
            return
          }
        })
        .catch((e) => {
          toast.error(`fanbook 用户授权信息获取错误，${e.message}`)
        })
    }

    // 业务诉求要走落地页，所以一定要强制绑定微信
    // PC 端会会略下面这个判断
    // 仅在 微信环境内非微信号登录触发
    if (namespace && isWechatBrowser && tenant?.v2WechatProviderId) {
      localStorage.removeItem('_authing_user')
      localStorage.removeItem('_authing_token')

      // 修复首次登录租户时，不会返回 identities，主动获取一次用户信息
      const res = await new AuthenticationClient({
        token: user.token,
        appId: AUTHING_APP_ID,
        appHost: AUTHING_APP_HOST
      }).getCurrentUser()

      if (
        // 判断用户是否授权过指定的公众号，要求落地页的授权微信号和课灵授权微信号保持一致
        !res.identities?.some(
          (item) => item.extIdpId === tenant.v2WechatProviderId
        )
      ) {
        const path = getBasePath(`/link-wechat?redirect=${redirect}`)
        router.push(path)
        return
      }
    }

    location.replace(redirect)
  }

  const { wechatAuthFn: onWechatAuth } = useWechatAuth({
    data: {
      tenant
    },
    onSuccess: onLogin,
    redirect:
      (router.query.redirect as string) ||
      getRedirect(_location, router.query as any)
  })

  const wechatLogin = () => {
    // Guard组件升级后可能出现 账号选择界面，该界面不存在协议勾选框
    const protocolNode = document.querySelector(
      '.authing-agreements .authing-ant-space-item .authing-ant-checkbox-input'
    ) as HTMLInputElement

    if (protocolNode && !protocolNode.checked) {
      toast.error('请先阅读并接受《服务条款》和《隐私协议》')
      return
    }

    onWechatAuth()
  }

  const onShowAllType = () => {
    console.log('hello')
  }

  const onLoad = () => {
    // setProtocolVisible.setTrue()
    setInitAuthing.setTrue()

    onAuthingScenesChange()

    changeLoginLogoStyle(tenant.logo)
    onObScenesChange(tenant.logo)
  }

  const onLoadError = (err: CommonMessage) => {
    toast.error(`初始化登录组件失败，${err.message}`)

    Sentry.setContext('url', {
      value: window.location.href
    })
    Sentry.setContext('Guard Config', tenant)
    Sentry.setContext('Authing Error', { ...err, scene: 'load' })
    Sentry.captureMessage(`Authing init fail`)
  }

  const onLoginError = (err: CommonMessage) => {
    toast.error(`登录失败，${err?.message}`)
    console.timeLog('time interval')
    console.log(err)
    Sentry.setContext(`Login params`, currentLoginInfo)
    Sentry.setContext(`Authing Error`, { ...err, scene: 'login' })
    Sentry.captureMessage(`Authing login fail ${err.code}`)
  }

  const onRegister = () => {
    toast.success(`验证邮件已发送至您的邮箱，请先验证邮箱`, {
      duration: 5
    })
  }

  const onRegisterError = (err: CommonMessage) => {
    toast.error(`注册失败，${err.message}`)
    Sentry.setContext(`Authing Error`, { ...err, scene: 'register' })
    Sentry.captureMessage(`Authing register fail`)
  }

  const onPhoneSend = () => {
    console.log('验证码已发送')
    console.time('time interval')
  }

  const onPhoneSendError = (err: CommonMessage) => {
    console.log('验证码发送错误', err)
    console.timeLog('time interval')
    Sentry.setContext(`Authing Error`, { ...err, scene: 'sms-send' })
    Sentry.captureMessage(`Authing send sms fail`)
  }

  useEffect(() => {
    const _query = router.query
    if (_query.login_type === 'sns') {
      setAll.setFalse()
    } else {
      setAll.setTrue()
    }
  }, [router.query])

  /**
   * @description 仅仅用于 UI 展示的逻辑，在切换注册或忘记时，隐藏自定义的微信登录按钮
   */
  const onAuthingScenesChange = () => {
    const targetNode = document.querySelector('.authing-g2-render-module')
    const callback = (mutationList, observer) => {
      for (const mutation of mutationList) {
        if (
          mutation.type === 'childList' &&
          [...mutation.addedNodes].some((itemNode) =>
            itemNode.classList.contains('g2-view-login')
          )
        ) {
          setCusLoginComponentVisibleActions.setTrue()
          return
        } else if (
          mutation.type === 'childList' &&
          !document.querySelector('.g2-view-login') &&
          cusLoginComponentVisible
        ) {
          setCusLoginComponentVisibleActions.setFalse()
        }
      }
    }

    const config = { childList: true, subtree: true }
    const observer = new MutationObserver(callback)
    observer.observe(targetNode, config)
  }

  useEffect(() => {
    guard.start('#authing-guard-container')

    guard.on('before-login', onBeforeLogin)
    guard.on('login', onLogin)
    guard.on('load', onLoad)
    guard.on('load-error', onLoadError)
    guard.on('login-error', onLoginError)
    guard.on('register', onRegister)
    guard.on('register-error', onRegisterError)
    guard.on('phone-send', onPhoneSend)
    guard.on('phone-send-error', onPhoneSendError)
    // TODO 待补充 注册事件触发前需要同意用户和隐私两协议
  }, [])

  return (
    <div className={style.wrapper}>
      <Head>
        <title>{`${tenant?.name}：登录`}</title>
      </Head>
      <div className={clsx(style['login__panel'])}>
        <div
          className={style.bg}
          style={{
            backgroundImage:
              'url(https://res.pandateacher.com/R71ZHVNH1648202389303.svg)'
          }}
        >
          <NEXTImage
            className={style['bottom-bg']}
            src="https://res.pandateacher.com/GV4BHQ991648202297985.svg"
            alt="装饰图片"
            layout="fill"
          />
          <img
            className={style['person-bg']}
            src="https://res.pandateacher.com/J9CG917L1648207415142.png"
            alt="装饰图片-人物"
          />
        </div>
        <div className={style['login-content-wrapper']}>
          <div className={clsx(style['login-content'])}>
            <div
              id="authing-guard-container"
              className={clsx('authing-agreements-checkbox__hidden', 'w-full')}
            ></div>

            {!isAll && (
              <div className="login__sns">
                <button
                  className="w-full text-white rounded border-1 border-solid py-2"
                  style={{ backgroundColor: '#44b549' }}
                  onClick={wechatLogin}
                >
                  <NEXTImage
                    src="https://res.pandateacher.com/THWZFVTM1658304240681.svg"
                    width="20"
                    height="20"
                    className="inline-block align-middle -mt-1"
                    alt="微信"
                  />
                  微信登录
                </button>

                <button
                  className="bg-white w-full border-1 py-2 rounded-md text-sm cursor-pointer inline-block mt-4"
                  onClick={onShowAllType}
                >
                  <NEXTImage
                    src="https://res.pandateacher.com/NIKRLDR91658306298809.svg"
                    width="20"
                    height="20"
                    className="inline-block align-middle -mt-1"
                    alt="账号登录"
                  />
                  账号登录
                </button>
              </div>
            )}

            {isInitAuthing && isShowWechatAuth && cusLoginComponentVisible && (
              <div className={style['login__more__container']}>
                <div
                  // isAll && isInitAuthing
                  className={clsx(style['login__more'], { hidden: isHidden })}
                >
                  <div className={clsx(style['more-login-type'])}>
                    其他登录方式
                  </div>
                  <button
                    className={clsx(
                      style['btn-sns__wx'],
                      '!flex justify-center items-center'
                    )}
                    onClick={wechatLogin}
                  >
                    <NEXTImage
                      src="https://res.pandateacher.com/DUBQ3ZMP1657787554360.svg"
                      width="20"
                      height="20"
                      className={style['btn-sns-icon']}
                      alt="微信"
                    />
                    <span className="ml-2 inline-block">微信登录</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// TODO 补充简略模式
// 判断逻辑为
// const isShowWechatAuth = computed(() => {
//   if (namespace) {
//     return !!(
//       (isWechatBroswer &&
//         wechatIpds.value[SocialConnectionProvider.WECHATMP]) ||
//       (isPcBrowser && wechatIpds.value[SocialConnectionProvider.WECHATPC])
//     )
//   } else {
//     return isWechatBroswer || isPcBrowser
//   }
// })

const AuthLoginPage = (props) => {
  const authingData = useContext(AuthingConfigContext)

  if (
    authingData?.AUTHING_APP_ID &&
    authingData?.AUTHING_APP_HOST &&
    props.tenant
  ) {
    return (
      <GuardProvider
        appId={authingData.AUTHING_APP_ID}
        host={authingData.AUTHING_APP_HOST}
        tenantId={props.tenant?.authingId}
        mode="normal"
        config={{
          title: props.tenant?.name ?? CLEWORD_DEFAULT_TITLE,
          logo:
            props.tenant?.logo ??
            'https://res.pandateacher.com/K9CBKKNV1636358161242.png',
          // registerMethods: [RegisterMethods.Email, RegisterMethods.Phone],
          // resetPwdMethods TODO 废弃属性 需要替换
          disableRegister: false,
          disableResetPwd: false,
          autoRegister: false,
          // defaultRegisterMethod: RegisterMethods.Email,
          socialConnections: [],
          // defaultLoginMethod: LoginMethods.PhoneCode,
          tenantId: props.tenant?.authingId
        }}
      >
        <Login
          isPcBrowser={props.isPcBrowser}
          isWechatBrowser={props.isWechatBrowser}
          _location={props._location}
          tenant={props.tenant}
          namespace={props.namespace}
          {...authingData}
        />
      </GuardProvider>
    )
  } else {
    return <div>loading</div>
  }
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const [namespace, _location] = getNamespaceAndLocationByServer(context)
  const ua = getUserAgentByServer(context)
  const token = getTokenByServe(context)
  let user = null

  if (token) {
    const [_user] = await Promise.all([handleGetUserByServer(context)]).catch(
      (err) => {
        console.log('[Page - Login]', err.message)
        return [null]
      }
    )
    user = _user
  }

  if (user && token) {
    return {
      redirect: {
        destination: getRedirect(_location as string, context.query as any),
        statusCode: 302
      }
    }
  }

  return {
    props: {
      user: user || null,
      _location,
      namespace,
      isWechatBrowser:
        ua && typeof ua === 'string' && !!ua.match(/MicroMessenger/),
      isPcBrowser:
        ua && typeof ua === 'string' && !ua.match(/AppleWebKit.*Mobile.*/)
    }
  }
}

export default AuthLoginPage
